import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Award,
  Briefcase,
  CheckCircle,
  Eye,
  EyeOff,
  Heart,
  Lock,
  Mail,
  MapPin,
  Phone,
  Plus,
  Shield,
  Star,
  User,
  Users,
  X,
  Zap
} from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    gender: '',
    
    // Step 2: Professional Details
    jobTitle: '',
    experience: '',
    totalExperience: '',
    location: '',
    address: {
      city: '',
      state: '',
      country: ''
    },
    preferredWorkMode: '',
    workAuthorization: '',
    workAuthorizationDetails: '',
    skills: [],
    newSkill: '',
    newSkillLevel: 'Intermediate',
    
    // Step 3: Preferences
    salaryExpectation: '',
    jobTypes: [],
    industries: [],
    
    // Additional
    terms: false,
    newsletter: true
  });

  const steps = [
    {
      id: 1,
      title: 'Personal Information',
      description: 'Tell us about yourself',
      icon: User,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 2,
      title: 'Professional Profile',
      description: 'Your career details',
      icon: Briefcase,
      gradient: 'from-emerald-500 to-green-500'
    },
    {
      id: 3,
      title: 'Job Preferences',
      description: 'What you\'re looking for',
      icon: Star,
      gradient: 'from-purple-500 to-violet-500'
    }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level (0-2 years)', icon: '🌱' },
    { value: 'mid', label: 'Mid Level (3-5 years)', icon: '🚀' },
    { value: 'senior', label: 'Senior Level (6-10 years)', icon: '⭐' },
    { value: 'expert', label: 'Expert Level (10+ years)', icon: '🏆' }
  ];

  const workModes = [
    { value: 'remote', label: 'Remote', icon: '🏠' },
    { value: 'hybrid', label: 'Hybrid', icon: '🔄' },
    { value: 'onsite', label: 'On-site', icon: '🏢' },
    { value: 'flexible', label: 'Flexible', icon: '⚡' }
  ];

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  const workAuthorizationOptions = [
    { value: 'Citizen/Permanent Resident', label: 'US Citizen / Permanent Resident' },
    { value: 'Work Visa', label: 'Work Visa (H1B, L1, etc.)' },
    { value: 'Other', label: 'Other (Please specify)' }
  ];

  const jobTypes = [
    { value: 'full-time', label: 'Full-time', icon: '⏰' },
    { value: 'part-time', label: 'Part-time', icon: '🕒' },
    { value: 'contract', label: 'Contract', icon: '📝' },
    { value: 'freelance', label: 'Freelance', icon: '💼' },
    { value: 'internship', label: 'Internship', icon: '🎓' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology', icon: '💻' },
    { value: 'healthcare', label: 'Healthcare', icon: '🏥' },
    { value: 'finance', label: 'Finance', icon: '💰' },
    { value: 'education', label: 'Education', icon: '📚' },
    { value: 'retail', label: 'Retail', icon: '🛍️' },
    { value: 'manufacturing', label: 'Manufacturing', icon: '🏭' },
    { value: 'marketing', label: 'Marketing', icon: '📱' },
    { value: 'consulting', label: 'Consulting', icon: '💡' }
  ];

  const popularSkills = [
    'JavaScript', 'Python', 'React', 'Node.js', 'SQL', 'AWS',
    'Project Management', 'Data Analysis', 'Digital Marketing', 'UX/UI Design',
    'Machine Learning', 'DevOps', 'Sales', 'Customer Service'
  ];

  const benefits = [
    {
      icon: Zap,
      title: "AI-Powered Matching",
      description: "Get personalized job recommendations"
    },
    {
      icon: Shield,
      title: "Privacy Protected",
      description: "Your information is secure with us"
    },
    {
      icon: Users,
      title: "50K+ Community",
      description: "Join our thriving professional network"
    },
    {
      icon: Award,
      title: "Success Guarantee",
      description: "95% of our users find jobs within 90 days"
    }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const addSkill = (skill) => {
    if (!formData.skills.some(s => s.name === skill)) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, { name: skill, level: prev.newSkillLevel }]
      }));
      setFormData(prev => ({ ...prev, newSkill: '' })); // Clear the input
    } else {
      toast.error('Skill already added.');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s.name !== skill)
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword || !formData.dateOfBirth || !formData.gender) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters long');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        return true;
      case 2:
        if (!formData.jobTitle || !formData.experience || !formData.totalExperience || !formData.location || !formData.workAuthorization || !formData.preferredWorkMode || !formData.address.city || !formData.address.state || !formData.address.country) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (formData.workAuthorization === 'Other' && !formData.workAuthorizationDetails) {
          toast.error('Please provide work authorization details');
          return false;
        }
        if (parseFloat(formData.totalExperience) < 0) {
          toast.error('Total experience must be a positive number');
          return false;
        }
        return true;
      case 3:
        if (!formData.terms) {
          toast.error('Please accept the terms and conditions');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) return;
    
    setIsLoading(true);

    try {
      // Format the data for backend
      const registrationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        jobTitle: formData.jobTitle,
        experience: formData.experience,
        totalExperience: parseInt(formData.totalExperience),
        location: formData.location,
        address: formData.address,
        workAuthorization: formData.workAuthorization,
        workAuthorizationDetails: formData.workAuthorizationDetails,
        preferredWorkMode: formData.preferredWorkMode,
        skills: formData.skills, // Already in correct format: [{name: string, level: string}]
        salaryExpectation: formData.salaryExpectation,
        jobTypes: formData.jobTypes,
        industries: formData.industries,
        newsletter: formData.newsletter
      };

      console.log('📤 Registration data being sent:', registrationData);
      
      const result = await register(registrationData);
      
      if (result.success) {
        toast.success('Account created successfully! Welcome to JobZentro!');
        navigate('/dashboard');
      } else {
        toast.error(result.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="grid md:grid-cols-2 gap-6">
        <div className="form-group">
          <label className="form-label">First Name *</label>
          <div className="relative">
            <User className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter your first name"
              className="input-luxury pl-12"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Last Name *</label>
          <div className="relative">
            <User className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter your last name"
              className="input-luxury pl-12"
              required
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Email Address *</label>
        <div className="relative">
          <Mail className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
            className="input-luxury pl-12"
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Phone Number</label>
        <div className="relative">
          <Phone className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
            className="input-luxury pl-12"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="form-group">
          <label className="form-label">Password *</label>
          <div className="relative">
            <Lock className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a secure password"
              className="input-luxury pl-12 pr-12"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Confirm Password *</label>
          <div className="relative">
            <Lock className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              className="input-luxury pl-12 pr-12"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="form-group">
          <label className="form-label">Date of Birth *</label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleChange}
            className="input-luxury"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Gender *</label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="input-luxury"
            required
          >
            <option value="">Select gender</option>
            {genderOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="form-group">
        <label className="form-label">Job Title / Position *</label>
        <div className="relative">
          <Briefcase className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleChange}
            placeholder="e.g., Software Engineer, Marketing Manager"
            className="input-luxury pl-12"
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Experience Level *</label>
        <div className="grid grid-cols-2 gap-3">
          {experienceLevels.map((level) => (
            <button
              key={level.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, experience: level.value }))}
              className={`p-4 rounded-2xl border-2 transition-all duration-300 text-left ${
                formData.experience === level.value
                  ? 'border-blue-500 bg-blue-50 gradient-text-primary'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{level.icon}</span>
                <div>
                  <div className="font-heading font-semibold text-sm">{level.label}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Total Years of Experience *</label>
        <input
          type="number"
          name="totalExperience"
          value={formData.totalExperience}
          onChange={handleChange}
          placeholder="e.g., 3"
          min="0"
          step="1"
          className="input-luxury"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Work Authorization *</label>
        <select
          name="workAuthorization"
          value={formData.workAuthorization}
          onChange={handleChange}
          className="input-luxury"
          required
        >
          <option value="">Select work authorization</option>
          {workAuthorizationOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Conditional work authorization details */}
      {formData.workAuthorization === 'Other' && (
        <div className="form-group">
          <label className="form-label">Work Authorization Details *</label>
          <input
            type="text"
            name="workAuthorizationDetails"
            value={formData.workAuthorizationDetails}
            onChange={handleChange}
            placeholder="Please specify your visa type (e.g., H1B, L1, O1, etc.)"
            className="input-luxury"
            required
          />
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Preferred Work Mode *</label>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {workModes.map((mode) => (
            <button
              key={mode.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, preferredWorkMode: mode.value }))}
              className={`p-4 rounded-2xl border-2 transition-all duration-300 text-center ${
                formData.preferredWorkMode === mode.value
                  ? 'border-emerald-500 bg-emerald-50 gradient-text-secondary'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="text-2xl mb-2">{mode.icon}</div>
              <div className="font-heading font-semibold text-sm">{mode.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Location *</label>
        <div className="relative">
          <MapPin className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="City, State or Remote"
            className="input-luxury pl-12"
            required
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="form-group">
          <label className="form-label">City *</label>
          <input
            type="text"
            name="address.city"
            value={formData.address.city}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              address: { ...prev.address, city: e.target.value }
            }))}
            placeholder="Enter city"
            className="input-luxury"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">State *</label>
          <input
            type="text"
            name="address.state"
            value={formData.address.state}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              address: { ...prev.address, state: e.target.value }
            }))}
            placeholder="Enter state"
            className="input-luxury"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Country *</label>
          <input
            type="text"
            name="address.country"
            value={formData.address.country}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              address: { ...prev.address, country: e.target.value }
            }))}
            placeholder="Enter country"
            className="input-luxury"
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Skills (Optional)</label>
        <div className="space-y-4">
          {/* Custom skill input */}
          <div className="flex space-x-2">
            <input
              type="text"
              name="newSkill"
              placeholder="Type a skill and press Enter"
              className="input-luxury flex-1"
              value={formData.newSkill}
              onChange={handleChange}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && formData.newSkill.trim()) {
                  addSkill(formData.newSkill.trim());
                }
              }}
            />
            <select 
              name="newSkillLevel"
              value={formData.newSkillLevel}
              onChange={handleChange}
              className="input-luxury w-24"
              style={{ width: '200px' }}
            >
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
            <button
              type="button"
              onClick={() => {
                if (formData.newSkill.trim()) {
                  addSkill(formData.newSkill.trim());
                }
              }}
              className="btn-outline px-4"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Selected skills */}
          <div className="flex flex-wrap gap-2">
            {formData.skills.map((skill) => (
              <span
                key={skill.name}
                className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
              >
                {skill.name} ({skill.level})
                <button
                  type="button"
                  onClick={() => removeSkill(skill.name)}
                  className="ml-2 hover:text-blue-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          
          {/* Popular skills suggestions */}
          <div>
            <p className="text-sm text-slate-600 mb-2">Popular skills:</p>
            <div className="flex flex-wrap gap-2">
              {popularSkills.filter(skill => !formData.skills.some(s => s.name === skill)).slice(0, 8).map((skill) => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      skills: [...prev.skills, { name: skill, level: prev.newSkillLevel }]
                    }));
                  }}
                  className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-slate-200 transition-colors"
                >
                  <Plus className="w-3 h-3 inline mr-1" />
                  {skill}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="form-group">
        <label className="form-label">Salary Expectation (Optional)</label>
        <select
          name="salaryExpectation"
          value={formData.salaryExpectation}
          onChange={handleChange}
          className="input-luxury"
        >
          <option value="">Select salary range</option>
          <option value="30-50k">$30,000 - $50,000</option>
          <option value="50-75k">$50,000 - $75,000</option>
          <option value="75-100k">$75,000 - $100,000</option>
          <option value="100-150k">$100,000 - $150,000</option>
          <option value="150k+">$150,000+</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Job Types (Select all that apply)</label>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {jobTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => handleArrayChange('jobTypes', type.value)}
              className={`p-4 rounded-2xl border-2 transition-all duration-300 text-center ${
                formData.jobTypes.includes(type.value)
                  ? 'border-purple-500 bg-purple-50 gradient-text'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="text-2xl mb-2">{type.icon}</div>
              <div className="font-heading font-semibold text-sm">{type.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Industries of Interest (Optional)</label>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {industries.map((industry) => (
            <button
              key={industry.value}
              type="button"
              onClick={() => handleArrayChange('industries', industry.value)}
              className={`p-3 rounded-xl border-2 transition-all duration-300 text-center ${
                formData.industries.includes(industry.value)
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="text-lg mb-1">{industry.icon}</div>
              <div className="font-medium text-xs">{industry.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t border-slate-200">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="terms"
            checked={formData.terms}
            onChange={handleChange}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            required
          />
          <label className="text-sm text-slate-600 leading-relaxed">
            I agree to the{' '}
            <Link to="/terms" className="gradient-text-primary font-medium hover:opacity-80">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="gradient-text-primary font-medium hover:opacity-80">
              Privacy Policy
            </Link>
            *
          </label>
        </div>

        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="newsletter"
            checked={formData.newsletter}
            onChange={handleChange}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label className="text-sm text-slate-600">
            Send me job alerts and career tips via email
          </label>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <Link to="/" className="inline-flex items-center space-x-3 mb-8 group">
              <div className="w-12 h-12 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-2xl font-display font-bold gradient-text">JobZentro</h1>
                <p className="text-xs text-slate-500 font-medium">An AI HRMS Platform</p>
              </div>
            </Link>

            <div className="space-y-2">
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900">
                Join Our Community
              </h2>
              <p className="text-lg text-slate-600 font-body">
                Create your account and start your career journey
              </p>
            </div>
          </motion.div>

          {/* Progress Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <div className="flex justify-between items-center">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                
                return (
                  <div key={step.id} className="flex-1 relative">
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                        isCompleted
                          ? 'bg-green-100 text-green-600'
                          : isActive
                          ? `bg-gradient-to-br ${step.gradient} text-white shadow-lg`
                          : 'bg-slate-100 text-slate-400'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : (
                          <Icon className="w-6 h-6" />
                        )}
                      </div>
                      <div className="mt-3 text-center">
                        <div className={`text-sm font-heading font-semibold ${
                          isActive ? 'gradient-text-primary' : 'text-slate-600'
                        }`}>
                          {step.title}
                        </div>
                        <div className="text-xs text-slate-500">{step.description}</div>
                      </div>
                    </div>
                    
                    {index < steps.length - 1 && (
                      <div className={`absolute top-6 left-1/2 w-full h-0.5 transition-colors duration-300 ${
                        currentStep > step.id ? 'bg-green-500' : 'bg-slate-200'
                      } -z-10`} />
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card-luxury p-8 bg-white/80 backdrop-blur-xl"
          >
            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`btn-outline ${
                    currentStep === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </button>

                {currentStep < 3 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-primary"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Create Account
                        <CheckCircle className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-center text-slate-600">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="gradient-text-primary font-semibold hover:opacity-80 transition-opacity"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 hero-background"></div>
        <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse-luxury"></div>

        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-lg space-y-8"
          >
            <div className="text-center space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/20">
                <Heart className="w-4 h-4 text-pink-300 mr-2" />
                <span className="text-sm font-medium">Join 50,000+ Professionals</span>
              </div>

              <h3 className="text-4xl lg:text-5xl font-display font-bold leading-tight text-shadow-lg">
                Your Career
                <span className="block text-white/90">Starts Here</span>
              </h3>

              <p className="text-xl text-white/80 leading-relaxed">
                Join the most innovative job platform and discover opportunities that match your ambitions.
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-6">
              <h4 className="text-lg font-heading font-semibold text-white/90">Why Choose JobZentro?</h4>
              <div className="space-y-4">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                      className="card-glass p-6 flex items-start space-x-4"
                    >
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h5 className="font-heading font-semibold text-white mb-1">{benefit.title}</h5>
                        <p className="text-white/80 text-sm">{benefit.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="grid grid-cols-3 gap-6 pt-6"
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">50K+</div>
                <div className="text-sm text-white/70">Active Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">10K+</div>
                <div className="text-sm text-white/70">Job Openings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">95%</div>
                <div className="text-sm text-white/70">Success Rate</div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-white/30 rounded-full animate-ping"></div>
        <div className="absolute top-3/4 left-1/3 w-3 h-3 bg-white/40 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-white/50 rounded-full animate-bounce"></div>
      </div>
    </div>
  );
};

export default Register; 